import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Check, X } from "lucide-react";

// Shared Terms & Conditions multi-select — used identically on Raise
// Transfer Order, Quotations (RaiseQuotation.jsx) and Purchase Orders,
// so the picker UI + combine-into-snapshot behavior lives in one place
// instead of being copy-pasted per page.
//
// Fetch the template list with `useActiveTermsTemplates()` and combine
// the result of this component's state into a document's saved T&C
// field with `buildTermsSnapshot()` (both in this same feature — see
// hooks/useActiveTermsTemplates.js and lib/termsSnapshot.js).
//
// Props:
//   templates        - active T&C templates, e.g. from useActiveTermsTemplates()
//   loading           - optional, shows a "Loading templates…" hint
//   selectedIds       - array of selected template ids
//   onSelectedIdsChange(ids)
//   overrides         - { [templateId]: editedBodyText }
//   onOverridesChange(overrides)
//   defaultText       - company Settings → default_terms fallback text
//   defaultOverride   - edited version of defaultText for this document
//   onDefaultOverrideChange(text)
//   extraText         - free-form one-off text typed on top of the selection
//   onExtraTextChange(text)
//   docLabel          - e.g. "order" / "quotation" / "purchase order" — used in copy only
export function TermsTemplatePicker({
  templates,
  loading = false,
  selectedIds,
  onSelectedIdsChange,
  overrides,
  onOverridesChange,
  defaultText,
  defaultOverride,
  onDefaultOverrideChange,
  extraText,
  onExtraTextChange,
  docLabel = "document",
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const ids = selectedIds || [];
  const selectedList = ids.map((id) => templates.find((t) => t.id === id)).filter(Boolean);

  const toggle = (id) => {
    onSelectedIdsChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const setOverride = (id, value) => {
    onOverridesChange({ ...(overrides || {}), [id]: value });
  };

  return (
    <div>
      <Label>Terms &amp; Conditions</Label>
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" className="w-full justify-between mt-1 font-normal" data-testid="terms-multiselect-trigger">
            <span className="truncate text-slate-700">
              {selectedList.length > 0
                ? `${selectedList.length} template${selectedList.length > 1 ? "s" : ""} selected`
                : <span className="text-slate-400">Select one or more T&amp;C templates…</span>}
            </span>
            <ChevronsUpDown className="w-4 h-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
          <Command>
            <CommandInput placeholder="Search templates…" />
            <CommandList>
              <CommandEmpty>
                {loading ? "Loading templates…" : "No templates found. Add them in Settings → Company Profile."}
              </CommandEmpty>
              <CommandGroup>
                {templates.map((t) => {
                  const checked = ids.includes(t.id);
                  return (
                    <CommandItem key={t.id} value={t.name} onSelect={() => toggle(t.id)} data-testid={`terms-opt-${t.id}`}>
                      <Check className={`w-4 h-4 mr-2 ${checked ? "opacity-100" : "opacity-0"}`} />
                      <span className="truncate">{t.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Editable preview of each selected T&C — edits are saved as a
          snapshot on this document only; the shared template in Settings
          is never modified from here. */}
      <div className="mt-2 space-y-2" data-testid="terms-preview-list">
        {selectedList.length > 0 ? (
          selectedList.map((t) => (
            <div key={t.id} className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700" data-testid={`terms-preview-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold text-slate-900 text-[13px]">{t.name}</div>
                <button
                  type="button"
                  onClick={() => onSelectedIdsChange(ids.filter((x) => x !== t.id))}
                  className="text-slate-400 hover:text-red-600 shrink-0"
                  aria-label={`Remove ${t.name}`}
                  data-testid={`terms-remove-${t.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <Textarea
                rows={4}
                value={overrides?.[t.id] ?? (t.body || t.content || "")}
                onChange={(e) => setOverride(t.id, e.target.value)}
                className="mt-1.5 text-[12px] bg-white"
                data-testid={`terms-edit-${t.id}`}
              />
            </div>
          ))
        ) : defaultText ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-700">
            <div className="font-semibold text-slate-900 text-[13px] not-italic">Using company default (editable for this {docLabel})</div>
            <Textarea
              rows={4}
              value={defaultOverride ?? defaultText}
              onChange={(e) => onDefaultOverrideChange(e.target.value)}
              className="mt-1.5 text-[12px] bg-white"
              data-testid="terms-edit-default"
            />
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-500 italic">
            No Terms &amp; Conditions selected. If none, the company default from Settings will be used.
          </div>
        )}

        {/* One-off text on top of whatever templates were selected — never
            written back to the shared template, only saved on this document. */}
        <div>
          <Label className="text-[11px] text-slate-500 font-normal">Additional notes for this {docLabel} (optional)</Label>
          <Textarea
            rows={2}
            value={extraText || ""}
            onChange={(e) => onExtraTextChange(e.target.value)}
            placeholder="Any one-off T&C text specific to this document…"
            className="mt-1 text-[12px] bg-white"
            data-testid="terms-extra-text"
          />
        </div>

        <p className="text-[10px] text-slate-400">Edits above apply only to this {docLabel} — the shared templates in Settings are unchanged.</p>
      </div>
    </div>
  );
}
